import { useSessionData } from '@confluence/session-data';

import { useFeedTypeSelector, useFeedTypeOverride, FeedType } from './useFeedTypeSelector';
import { useFollowingFeedWithSortByStorage } from './useFollowingFeedWithSortByStorage';
import { usePopularFeedWithTimeGranularityStorage } from './usePopularFeedWithTimeGranularityStorage';

/**
 * This hook is used to determine if a user has any feed content on the Home Page.
 *
 * If the user is not logged in, then the feed is considered empty.
 * If the user is logged in, then:
 * - If the user has selected a feed type preference, we check if that feed type has any entries.
 * - If the user has NOT selected a feed type preference, we default to checking if the user has any FOLLOWING feed first, and then POPULAR if there's no FOLLOWING.
 *
 * This is a simplified version of the logic used in the Feed.tsx component to determine if the feed is empty when sending analytics events in useFeedRenderedTrackEvent hook.
 * @returns {count: number, hasRetrievedFeedCount: boolean}
 */
export const useEmptyFeed = () => {
	const { isLoggedIn } = useSessionData();
	const { feedTypeOverride } = useFeedTypeOverride();
	const {
		loading: loadingPreferences,
		feedType,
		hasSelectedFeedTypePreference,
	} = useFeedTypeSelector();
	const filterOrOverride = feedTypeOverride || feedType;

	const { followingFeedQuery } = useFollowingFeedWithSortByStorage();
	const { popularFeedQuery } = usePopularFeedWithTimeGranularityStorage(true);

	// If the user is not logged in, they should not see the feed section. So we don't need to check if the feed is empty.
	if (!isLoggedIn) {
		return {
			hasFeed: false,
		};
	}

	if (loadingPreferences) {
		return {
			hasFeed: false,
		};
	}

	const hasFollowingFeed =
		!followingFeedQuery?.loading && Boolean(followingFeedQuery?.data?.feed?.nodes.length);
	const hasPopularFeed =
		!popularFeedQuery?.loading && Boolean(popularFeedQuery?.data?.popularFeed?.nodes.length);

	// If user has NOT selected a feed type preference, we default to checking if the user has any FOLLOWING feed first, and then POPULAR if there's no FOLLOWING.
	if (!hasSelectedFeedTypePreference) {
		return {
			hasFeed: hasFollowingFeed || hasPopularFeed,
		};
	}

	// If user has selected a feed type preference, we first check if the overridden type is POPULAr
	if (filterOrOverride === FeedType.POPULAR) {
		return { hasFeed: hasPopularFeed };
	}

	// Otherwise, we default to checking if the user has any FOLLOWING feed
	return { hasFeed: hasFollowingFeed };
};
